import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Image } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN, DEFAULT_THUMB } from '../../utils/utility';
import { deleteCategory, getCategories, getAllCategories } from '../../services/category';
import isEqual from 'lodash/isEqual';

const itemName = 'Danh mục';
const createItemLink = '/cms/quan-ly-danh-muc/them-danh-muc';
const editItemLink = '/cms/quan-ly-danh-muc/sua-danh-muc';
const { Text } = Typography;

// helper: convert HTML to plain text and truncate
const toPlain100 = (html, limit = 100) => {
  if (!html || typeof html !== 'string') return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  const text = (el.textContent || el.innerText || '').trim();
  if (text.length <= limit) return text;
  return text.slice(0, limit) + '...';
};

const CategoryList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const CATEGORY_TYPES = useMemo(() => ([
    { value: 1, label: 'Danh mục bài viết' },
    { value: 2, label: 'Danh mục khóa học' },
  ]), []);

  const makeSlug = (str) => {
    if (!str) return '';
    let s = String(str)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D');
    s = s.toLowerCase().trim();
    s = s.replace(/[^a-z0-9]+/g, '-')
         .replace(/^-+|-+$/g, '');
    return s;
  };
  const externalCategoryUrl = (title, id) => `http://toancohien.vn/web/category/${makeSlug(title)}-${id}`;

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const parentStr = searchParams.get('parent_id');
    const parentVal = parentStr !== null ? Number(parentStr) : -1;
    const typeStr = searchParams.get('type');
    const typeVal = typeStr !== null ? Number(typeStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (parentVal > 0) Object.assign(next, { parent_id: parentVal });
    if (typeVal > 0) Object.assign(next, { type: [typeVal] });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      parent_id: parentVal > 0 ? parentVal : -1,
      type: typeVal > 0 ? typeVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (typeof params.parent_id === 'number' && params.parent_id > 0) {
      sp.parent_id = String(params.parent_id);
    }
    if (Array.isArray(params.type) && params.type.length > 0 && params.type[0] > 0) {
      sp.type = String(params.type[0]);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const { data: dataCategories, isPending, refetch } = useQuery({ queryKey: ['dataCategories', params], queryFn: () => getCategories(params) });

  // Supporting data: all categories map for parent name
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const categoryMap = useMemo(() => {
    return (allCategoriesRes?.data || []).reduce((acc, cur) => {
      acc[cur?.id] = cur;
      return acc;
    }, {});
  }, [allCategoriesRes]);
  // Build hierarchical options for Parent filter: 'Parent — Child — Leaf'
  const parentFilterOptions = useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const byId = {};
    const roots = [];
    all.forEach((c) => { byId[c.id] = { ...c, children: [] }; });
    all.forEach((c) => {
      const pid = c.parent_id || 0;
      if (pid && byId[pid]) byId[pid].children.push(byId[c.id]); else roots.push(byId[c.id]);
    });
    const sortRecursive = (node) => {
      node.children.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      node.children.forEach(sortRecursive);
    };
    roots.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    roots.forEach(sortRecursive);
    const res = [{ value: -1, label: 'Tất cả' }];
    const dfs = (node, path) => {
      const current = [...path, node.title || ''];
      res.push({ value: node.id, label: current.join(' — ') });
      node.children.forEach((ch) => dfs(ch, current));
    };
    roots.forEach((r) => dfs(r, []));
    return res;
  }, [allCategoriesRes]);

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteCategory,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Xóa ${itemName} thành công!` });
        refetch();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });
    if (values?.parent_id > 0) Object.assign(temp, { parent_id: values?.parent_id });
    if (values?.type > 0) Object.assign(temp, { type: [values?.type] });

    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetch();
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({ style: { maxWidth: 70 } }),
      onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id, type }) => (
        Number(type) === 2 ? (
          <a href={externalCategoryUrl(title, id)} target="_blank" rel="noreferrer" className="text-[#1677FF]">{title}</a>
        ) : (
          <span>{title}</span>
        )
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Danh mục cha',
      dataIndex: 'parent_id',
      key: 'parent',
      render: (_, { parent_id }) => {
        const parent = parent_id ? categoryMap[parent_id] : null;
        return <span>{parent?.title || '—'}</span>;
      },
      minWidth: 180,
      width: 220,
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      render: (type) => CATEGORY_TYPES.find((t) => t.value === Number(type))?.label || '—',
      minWidth: 160,
      width: 200,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={(AppSelect.STATUS || []).find((item) => item?.value === status)?.color}>
          {(AppSelect.STATUS || []).find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 90,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
          {record?.status !== 2 && (
            <Button type="text" onClick={() => mutateDelete(record?.id)}>
              <DeleteOutlined />
            </Button>
          )}
        </Space>
      ),
      width: 90,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link>
      </div>

      <Form name="search_categories" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS || [])]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="type" label={<Text>Loại danh mục</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...CATEGORY_TYPES]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="parent_id" label={<Text>Danh mục cha</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                placeholder="Chọn danh mục cha"
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={parentFilterOptions}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['keyword', 'status', 'parent_id'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataCategories?.data ?? []} totalItem={dataCategories?.total ?? 0} loading={isPending} params={params} setParams={setParams} />
    </div>
  );
};

export default CategoryList;
